import { ClipboardCheck, Send } from "lucide-react";
import { useI18n } from "@project/lib/use-i18n";
import { formatDate } from "@project/lib/date-format";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@project/components/ui/dialog";

export type AdmissionInquiryConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inquirerName?: string;
  elderName?: string;
  applicationDate?: string;
  submitting?: boolean;
  onConfirm: () => void | Promise<void>;
};

function SummaryItem({ label, value }: { label: string; value?: string }) {
  if (!value) {
    return null;
  }

  return (
    <div className="flex-1 px-5 py-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
        {label}
      </p>
      <p className="mt-1 font-medium text-foreground">{value}</p>
    </div>
  );
}

export function AdmissionInquiryConfirmDialog({
  open,
  onOpenChange,
  inquirerName,
  elderName,
  applicationDate,
  submitting = false,
  onConfirm,
}: AdmissionInquiryConfirmDialogProps) {
  const { t } = useI18n();
  const hasSummary = [inquirerName, elderName, applicationDate].some(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-primary/20 max-w-2xl gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <div className="from-primary/15 via-primary/5 bg-gradient-to-b to-background px-8 pb-7 pt-9 text-center">
          <div className="bg-primary/15 ring-primary/10 relative mx-auto mb-5 grid h-20 w-20 place-items-center rounded-full ring-4">
            <ClipboardCheck className="h-10 w-10 text-primary" />
          </div>

          <DialogTitle className="font-serif text-2xl font-bold text-foreground sm:text-3xl">
            {t("account.inquiryForm.confirmDialog.title")}
          </DialogTitle>
          <DialogDescription className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t("account.inquiryForm.confirmDialog.description")}
          </DialogDescription>

          {hasSummary && (
            <div className="bg-card/80 mx-auto mt-6 flex max-w-xl flex-col divide-y divide-border overflow-hidden rounded-xl border border-border text-left shadow-sm sm:flex-row sm:divide-x sm:divide-y-0">
              <SummaryItem
                label={t("account.inquiryForm.confirmDialog.inquirer")}
                value={inquirerName}
              />
              <SummaryItem
                label={t("account.inquiryForm.confirmDialog.elder")}
                value={elderName}
              />
              <SummaryItem
                label={t("account.inquiryForm.confirmDialog.applicationDate")}
                value={formatDate(applicationDate) || undefined}
              />
            </div>
          )}
        </div>

        <div className="space-y-5 px-8 pb-8">
          {/* <div className="border-primary/30 bg-primary/5 rounded-xl border border-dashed p-4">
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t("account.inquiryForm.confirmDialog.consentNote")}
            </p>
          </div> */}

          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              className="h-11 flex-1 rounded-xl hover:bg-primary hover:text-primary-foreground"
              onClick={() => onOpenChange(false)}
            >
              {t("account.inquiryForm.confirmDialog.goBack")}
            </Button>
            <Button
              type="button"
              disabled={submitting}
              className="hover:bg-gold/90 h-11 flex-1 gap-2 rounded-xl bg-gold text-gold-foreground"
              onClick={() => void onConfirm()}
            >
              <Send className="h-4 w-4" />
              {submitting
                ? t("account.inquiryForm.confirmDialog.submitting")
                : t("account.inquiryForm.confirmDialog.confirmSubmit")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
